/**
 * Internal-traffic tagging for Privisy's own analytics.
 *
 * Opening any page with `?internal=1` marks that browser as internal: it gets
 * a first-party `ga_internal` cookie scoped to all of getprivisy.com, and every
 * hit the Google tag sends from it carries `traffic_type=internal` (`tt=internal`
 * on the /g/collect request), which the GA4 property's active internal-traffic
 * filter drops. `?internal=0` undoes it. This complements the IP-based rule
 * rather than replacing it: both set the same value, and the cookie keeps
 * working after the ISP rotates the address that rule matches.
 *
 * It is an inline-script string, not a function, because it has to run inside
 * each app's Google tag bootstrap: after the gtag stub exists and before
 * gtag('config'), or the page_view that config sends on load goes out
 * untagged. www interpolates it into the stub in app/layout.tsx. The
 * dashboard's stub in index.html runs before its bundle and cannot import
 * this, so it carries a verbatim copy; gtagBootstrap.test.ts fails if the two
 * drift.
 *
 * The cookie is rewritten on every tagged visit because Safari caps
 * script-set cookies at 7 days. The whole body sits in a try/catch because
 * document.cookie can throw (a sandboxed iframe), and an uncaught error would
 * abort the rest of the bootstrap, including the config call every Ads
 * conversion depends on.
 *
 * Once read, the `internal` parameter is removed from the address bar with
 * history.replaceState (keeping history.state, which Next's router owns, and
 * the dashboard's hash route). Left in place, any link copied from a tagged
 * page (into Slack, a doc, an outreach email) would opt everyone who clicks it
 * in for a year without their knowing.
 *
 * Both apps run this only behind their consent gate (isTrackingAllowed()), so
 * in a browser that sends GPC or has opted out it never runs: `?internal=1`
 * does nothing there, and neither does `?internal=0`, so a cookie set before
 * the opt-out stays behind. That is harmless, since gtag never loads in such a
 * browser, and isInternalBrowser() only matters for hits that gate lets out.
 *
 * `traffic_type` is a GA4 parameter that Google Ads ignores, so
 * fireAdsConversion() in tracking.ts checks isInternalBrowser() and skips the
 * conversion itself; otherwise a teammate's test scan would still feed Smart
 * Bidding.
 */
export const INTERNAL_TRAFFIC_SNIPPET = String.raw`(function () {
  try {
    var attrs = '; path=/; SameSite=Lax' +
      (/(^|\.)getprivisy\.com$/.test(location.hostname) ? '; domain=.getprivisy.com' : '');
    var flag = new URLSearchParams(location.search).get('internal');
    if (flag === '0') {
      document.cookie = 'ga_internal=; max-age=0' + attrs;
    } else if (flag === '1' || /(^| )ga_internal=1(;|$)/.test(document.cookie)) {
      document.cookie = 'ga_internal=1; max-age=31536000' + attrs;
      gtag('set', { traffic_type: 'internal' });
    }
    if (flag !== null && history.replaceState) {
      var url = new URL(location.href);
      url.searchParams.delete('internal');
      history.replaceState(history.state, '', url.toString());
    }
  } catch (e) {}
})();`;

/** The first-party cookie INTERNAL_TRAFFIC_SNIPPET writes to opt a browser in. */
export const INTERNAL_TRAFFIC_COOKIE = 'ga_internal';

/**
 * Whether this browser has opted in as internal traffic. The match is anchored
 * the same way the snippet's is, so `ga_internal=10` or `xga_internal=1` does
 * not count. Never throws: a document.cookie that throws reads as not internal.
 */
export function isInternalBrowser(): boolean {
  try {
    return typeof document !== 'undefined' && /(^| )ga_internal=1(;|$)/.test(document.cookie);
  } catch {
    return false;
  }
}
